import { useAtom } from "jotai";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { brandColorAtom, themeAtom } from "@/core/state";
import { BRAND_PRESETS, DEFAULT_BRAND, normalizeHex } from "@dozka/ui";
import { Button, Carte, Etiquette, Grille, Saisie } from "@/shared/ui";

import { TailleAffichagePicker } from "./TailleAffichagePicker";
import { WallpaperPicker } from "./WallpaperPicker";

/**
 * Les réglages de l'espace de travail — mode, couleur d'accent, fond d'écran,
 * taille de l'affichage. Un seul composant, rendu à deux endroits : l'accueil
 * du chatteur (premier passage) et la modale « Personnaliser » du rail. Tout
 * s'applique EN DIRECT (atomes globaux) : l'aperçu à côté, et le cockpit
 * derrière, changent au clic.
 *
 * Sorti de `ThemePickerModal` le 07-10-2026, quand l'accueil en a eu besoin.
 *
 * Dessin (coque, 01-11-2026) : chaque choix est une `Carte` cliquable, la
 * choisie porte la marque (`actif`) ; plus d'anneau gris, plus de pastille
 * ronde « coché » dessinée à la main.
 */
type ThemeId = "dark" | "light" | "glass";

interface ThemeOption {
  id: ThemeId;
  /** Aperçu : couleur de canvas + panel + trait (l'accent vient en live). Ce sont des
   *  COULEURS-DONNÉES — ce à quoi RESSEMBLE chaque mode, y compris ceux qu'on ne porte pas : un
   *  jeton ne dirait que le mode courant. */
  preview: { canvas: string; panel: string; line: string };
  /** Verre dépoli : l'aperçu rend un panneau translucide + flou sur un fond
   *  dégradé (simule le wallpaper) au lieu de surfaces pleines. */
  glass?: boolean;
}

const THEMES: ThemeOption[] = [
  { id: "dark", preview: { canvas: "#131313", panel: "#090909", line: "#2a2a2a" } },
  { id: "light", preview: { canvas: "#f4f4f4", panel: "#ffffff", line: "#dcdcdc" } },
  {
    id: "glass",
    glass: true,
    preview: {
      canvas: "linear-gradient(135deg, #3b3d44, #15161a)",
      panel: "rgba(255, 255, 255, 0.10)",
      line: "rgba(255, 255, 255, 0.30)",
    },
  },
];

export function ReglagesDuTheme() {
  const { t } = useTranslation();
  const [theme, setTheme] = useAtom(themeAtom);
  const [brandColor, setBrandColor] = useAtom(brandColorAtom);

  const liveBrand = (brandColor && normalizeHex(brandColor)) || DEFAULT_BRAND;
  const isDefaultBrand = !brandColor || normalizeHex(brandColor) === DEFAULT_BRAND;

  // Sélectionner le défaut = retirer l'override (null) → retombe sur le thème.
  const pickBrand = (hex: string) =>
    setBrandColor(normalizeHex(hex) === DEFAULT_BRAND ? null : hex);

  return (
    <div className="flex flex-col gap-6">
      {/* 1. Mode (sombre / clair / verre) */}
      <section className="flex flex-col gap-3">
        <SectionLabel>{t("shell.themePicker.sections.mode")}</SectionLabel>
        <Grille min={88} ecart={8}>
          {THEMES.map((opt) => {
            const isActive = opt.id === theme;
            return (
              <Carte
                key={opt.id}
                remplissage="sm"
                actif={isActive}
                aria-pressed={isActive}
                onClick={() => setTheme(opt.id)}
                className="flex flex-col gap-2"
              >
                <ThemeThumbnail preview={opt.preview} accent={liveBrand} glass={opt.glass} />
                <span className="text-[13px] font-medium">
                  {t(`shell.themePicker.themes.${opt.id}`)}
                </span>
              </Carte>
            );
          })}
        </Grille>
      </section>

      {/* 2. Couleur d'accent (marque) */}
      <section className="flex flex-col gap-3">
        <div className="flex min-h-7 items-center justify-between gap-2">
          <SectionLabel>{t("shell.themePicker.sections.accent")}</SectionLabel>
          {!isDefaultBrand && (
            <Button variant="ghost" size="sm" onClick={() => setBrandColor(null)}>
              {t("shell.themePicker.accent.reset")}
            </Button>
          )}
        </div>
        <AccentColorPicker value={liveBrand} onPick={pickBrand} />
      </section>

      {/* 3. Fond d'écran */}
      <section className="flex flex-col gap-3">
        <SectionLabel>{t("shell.themePicker.sections.wallpaper")}</SectionLabel>
        <WallpaperPicker />
      </section>

      {/* 4. Taille de l'affichage — l'espace de travail : combien de choses
          tiennent à l'écran. L'aperçu la rend à l'échelle réelle. */}
      <section className="flex flex-col gap-3">
        <SectionLabel>
          {t("shell.settings.preferences.tailleAffichage.title", {
            defaultValue: "Taille de l'affichage",
          })}
        </SectionLabel>
        <TailleAffichagePicker />
      </section>
    </div>
  );
}

/** Le titre d'un groupe de réglages — l'étiquette de la librairie, en titre de section. */
export function SectionLabel({ children }: { children: ReactNode }) {
  return <Etiquette as="h3">{children}</Etiquette>;
}

/**
 * Picker de couleur de marque : les presets de la marque, la couleur libre (le sélecteur du
 * système) et son code — le `ChoixDeCouleur` de la librairie. Tout changement applique la couleur
 * en direct (live preview) ; la librairie ne commite qu'une fois par image pendant un glissé.
 */
function AccentColorPicker({
  value,
  onPick,
}: {
  value: string;
  onPick: (hex: string) => void;
}) {
  const { t } = useTranslation();
  return (
    <Saisie.ChoixDeCouleur
      valeur={value}
      onChange={onPick}
      couleurs={BRAND_PRESETS.map((p) => ({ valeur: p.hex, nom: t(p.labelKey) }))}
      libreLabel={t("shell.themePicker.accent.customLabel")}
      codeLabel={t("shell.themePicker.accent.customLabel")}
    />
  );
}

/**
 * Mini-aperçu d'un mode : canvas + panel + un trait d'accent (= la couleur de
 * marque courante) pour refléter l'accent choisi. Un dessin d'illustration :
 * les traits sont des aplats, sans contour.
 */
function ThemeThumbnail({
  preview,
  accent,
  glass,
}: {
  preview: ThemeOption["preview"];
  accent: string;
  glass?: boolean;
}) {
  return (
    <div
      className="aspect-[4/3] w-full overflow-hidden sf-arrondi-ligne p-1.5 shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--color-fg)_14%,transparent)]"
      style={{ background: preview.canvas }}
    >
      <div
        className={
          "flex h-full w-full flex-col gap-1 p-1.5" +
          (glass ? " backdrop-blur-md backdrop-saturate-150" : "")
        }
        style={{ background: preview.panel }}
      >
        <div className="h-1 w-1/3" style={{ background: accent }} />
        <div className="h-1 w-2/3" style={{ background: preview.line }} />
        <div className="h-1 w-1/2" style={{ background: preview.line }} />
      </div>
    </div>
  );
}
